'use client'

import clsx from 'clsx'
import {
    KeyboardEvent as ReactKeyboardEvent,
    MouseEvent as ReactMouseEvent,
    ReactNode,
    PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'

export type RangeSliderLabel = 'none' | 'bottom' | 'tooltip'

export type RangeSliderValue = number | [number, number]

export interface RangeSliderProps {
    value?: RangeSliderValue
    defaultValue?: RangeSliderValue
    onChange?: (value: RangeSliderValue) => void
    onChangeEnd?: (value: RangeSliderValue) => void
    min?: number
    max?: number
    step?: number
    label?: RangeSliderLabel
    formatLabel?: (value: number) => ReactNode
    disabled?: boolean
    id?: string
    name?: string
    'aria-label'?: string
    className?: string
}

type Handle = 'lower' | 'upper'

const ROOT_PADDING: Record<RangeSliderLabel, string> = {
    none: 'py-spacing-md',
    bottom: 'pb-[38px] pt-spacing-md',
    tooltip: 'pb-spacing-md pt-[46px]',
}

const defaultFormatLabel = (val: number): ReactNode => `${val}%`

export const RangeSlider = ({
    value,
    defaultValue,
    onChange,
    onChangeEnd,
    min = 0,
    max = 100,
    step = 1,
    label = 'none',
    formatLabel = defaultFormatLabel,
    disabled = false,
    id,
    name,
    'aria-label': ariaLabel,
    className,
}: RangeSliderProps) => {
    const isControlled = value !== undefined

    const [internalValue, setInternalValue] = useState<RangeSliderValue>(() => {
        if (defaultValue !== undefined) {
            return defaultValue
        }
        return min
    })

    const currentValue: RangeSliderValue = value ?? internalValue
    const isRange = Array.isArray(currentValue)
    const [lowerValue, upperValue]: [number, number] = Array.isArray(currentValue)
        ? currentValue
        : [min, currentValue]

    const trackRef = useRef<HTMLDivElement>(null)
    const [dragging, setDragging] = useState<Handle | null>(null)

    const snapToStep = useCallback(
        (val: number) => Math.round((val - min) / step) * step + min,
        [min, step],
    )

    const clampValue = useCallback(
        (val: number, handle: Handle) => {
            const clamped = Math.max(min, Math.min(max, val))
            if (!isRange) {
                return clamped
            }
            if (handle === 'lower') {
                return Math.min(clamped, upperValue)
            }
            return Math.max(clamped, lowerValue)
        },
        [min, max, isRange, lowerValue, upperValue],
    )

    const valueToPercent = useCallback(
        (val: number) => {
            if (max === min) {
                return 0
            }
            return ((val - min) / (max - min)) * 100
        },
        [min, max],
    )

    const clientXToValue = useCallback(
        (clientX: number) => {
            const track = trackRef.current
            if (!track) {
                return min
            }
            const rect = track.getBoundingClientRect()
            const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
            return snapToStep(percent * (max - min) + min)
        },
        [min, max, snapToStep],
    )

    const buildValue = useCallback(
        (handle: Handle, next: number): RangeSliderValue => {
            if (!isRange) {
                return next
            }
            if (handle === 'lower') {
                return [next, upperValue]
            }
            return [lowerValue, next]
        },
        [isRange, lowerValue, upperValue],
    )

    const updateValue = useCallback(
        (next: RangeSliderValue) => {
            if (!isControlled) {
                setInternalValue(next)
            }
            onChange?.(next)
        },
        [isControlled, onChange],
    )

    const handlePointerMove = useCallback(
        (e: PointerEvent) => {
            if (!dragging || disabled) {
                return
            }
            const next = clampValue(clientXToValue(e.clientX), dragging)
            updateValue(buildValue(dragging, next))
        },
        [dragging, disabled, clampValue, clientXToValue, updateValue, buildValue],
    )

    const handlePointerUp = useCallback(() => {
        if (dragging) {
            setDragging(null)
            onChangeEnd?.(currentValue)
        }
    }, [dragging, onChangeEnd, currentValue])

    useEffect(() => {
        if (!dragging) {
            return undefined
        }
        document.addEventListener('pointermove', handlePointerMove)
        document.addEventListener('pointerup', handlePointerUp)
        return () => {
            document.removeEventListener('pointermove', handlePointerMove)
            document.removeEventListener('pointerup', handlePointerUp)
        }
    }, [dragging, handlePointerMove, handlePointerUp])

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>, handle: Handle) => {
        if (disabled) {
            return
        }
        const current = handle === 'lower' ? lowerValue : upperValue
        let next: number

        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                next = current - step
                break
            case 'ArrowRight':
            case 'ArrowUp':
                next = current + step
                break
            case 'PageDown':
                next = current - step * 10
                break
            case 'PageUp':
                next = current + step * 10
                break
            case 'Home':
                next = min
                break
            case 'End':
                next = max
                break
            default:
                return
        }

        e.preventDefault()
        const clamped = clampValue(next, handle)
        const nextValue = buildValue(handle, clamped)
        updateValue(nextValue)
        onChangeEnd?.(nextValue)
    }

    const handleTrackClick = (e: ReactMouseEvent<HTMLDivElement>) => {
        if (disabled) {
            return
        }
        const raw = clientXToValue(e.clientX)
        let handle: Handle = 'upper'
        if (isRange && Math.abs(raw - lowerValue) <= Math.abs(raw - upperValue)) {
            handle = 'lower'
        }
        const nextValue = buildValue(handle, clampValue(raw, handle))
        updateValue(nextValue)
        onChangeEnd?.(nextValue)
    }

    const lowerPercent = valueToPercent(lowerValue)
    const upperPercent = valueToPercent(upperValue)
    const fillStart = isRange ? lowerPercent : 0

    const getAriaBounds = (handle: Handle): [number, number] => {
        if (!isRange) {
            return [min, max]
        }
        if (handle === 'lower') {
            return [min, upperValue]
        }
        return [lowerValue, max]
    }

    const getAriaLabel = (handle: Handle): string | undefined => {
        if (ariaLabel) {
            return ariaLabel
        }
        if (!isRange) {
            return undefined
        }
        return handle === 'lower' ? 'Minimum' : 'Maximum'
    }

    const getHandleId = (handle: Handle): string | undefined => {
        if (!id) {
            return undefined
        }
        if (isRange) {
            return `${id}-${handle}`
        }
        return id
    }

    const renderValueLabel = (val: number) => {
        const content = formatLabel(val)
        if (label === 'bottom') {
            return (
                <span className="pointer-events-none absolute left-1/2 top-full mt-2.5 -translate-x-1/2 whitespace-nowrap text-md font-medium text-text-primary">
                    {content}
                </span>
            )
        }
        if (label === 'tooltip') {
            return (
                <span className="pointer-events-none absolute bottom-full left-1/2 mb-2.5 -translate-x-1/2 whitespace-nowrap rounded-radius-md bg-background-bg-primary px-spacing-md py-spacing-sm text-xs font-semibold text-text-secondary shadow-lg ring-1 ring-alpha-black-10">
                    {content}
                </span>
            )
        }
        return null
    }

    const renderHandle = (handle: Handle) => {
        const val = handle === 'lower' ? lowerValue : upperValue
        const [ariaMin, ariaMax] = getAriaBounds(handle)
        const formatted = formatLabel(val)
        const isActive = dragging === handle

        const onPointerDown = (e: ReactPointerEvent<HTMLSpanElement>) => {
            if (disabled) {
                return
            }
            e.preventDefault()
            e.currentTarget.focus()
            setDragging(handle)
        }

        return (
            <span
                className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${valueToPercent(val)}%` }}>
                <span
                    id={getHandleId(handle)}
                    role="slider"
                    tabIndex={disabled ? -1 : 0}
                    aria-valuemin={ariaMin}
                    aria-valuemax={ariaMax}
                    aria-valuenow={val}
                    aria-valuetext={
                        typeof formatted === 'string' || typeof formatted === 'number'
                            ? String(formatted)
                            : undefined
                    }
                    aria-label={getAriaLabel(handle)}
                    aria-disabled={disabled || undefined}
                    className={clsx(
                        'block size-6 rounded-radius-full border-2 border-solid border-brand-600 bg-base-white shadow-md',
                        'touch-none outline-none transition-shadow',
                        'focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
                        disabled && 'cursor-not-allowed',
                        !disabled && isActive && 'cursor-grabbing',
                        !disabled && !isActive && 'cursor-grab',
                    )}
                    onPointerDown={onPointerDown}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => handleKeyDown(e, handle)}
                />
                {renderValueLabel(val)}
            </span>
        )
    }

    return (
        <div
            className={clsx(
                'relative w-full font-body',
                ROOT_PADDING[label],
                disabled && 'cursor-not-allowed opacity-50',
                className,
            )}>
            <div
                ref={trackRef}
                className="relative h-2 rounded-radius-full bg-background-bg-quaternary"
                onClick={handleTrackClick}>
                <div
                    className="absolute inset-y-0 rounded-radius-full bg-background-bg-brand-solid"
                    style={{ left: `${fillStart}%`, width: `${upperPercent - fillStart}%` }}
                />
                {isRange && renderHandle('lower')}
                {renderHandle('upper')}
            </div>

            {name && (
                <>
                    {isRange && <input type="hidden" name={`${name}[0]`} value={lowerValue} />}
                    <input type="hidden" name={isRange ? `${name}[1]` : name} value={upperValue} />
                </>
            )}
        </div>
    )
}
